import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageContainer, InfoIcon, Infos, Layout, NowMarker, SubTitle, Time, Title, tvGuideImageRatio } from './GridEntry.styles';
import SvgInfo from '../../../../../play-core/icons/Info';
import { formatTime } from '../../../utils/date-formatter';
import { PlayImage } from '../../Image/PlayImage';
import { ProgramEntryPlayLink } from '../../Program/ProgramEntry/ProgramEntryPlayLink';
import { gridChannelWidth, hoverItemWidth, localTimeSpan, minWidthForImage, positionInTvDay } from '../tv-guide-types';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const GridEntry = ({
  showImage,
  pixelsPerMinute,
  program,
  channel,
  activeChannelId,
  date,
  now,
  onSelectProgram,
  scrollRef,
  analyticsPageId
}) => {
  const {
    t
  } = useTranslation();
  const itemRef = useRef(null);
  const actualLeft = positionInTvDay(program.startTime, date, pixelsPerMinute);
  const actualRight = positionInTvDay(program.endTime, date, pixelsPerMinute);
  const width = actualRight - actualLeft;
  const isNow = now ? now >= actualLeft && now < actualRight : false;
  const isActive = channel.id === activeChannelId && isNow;
  const [left, setLeft] = useState(actualLeft);
  const [minWidth, setMinWidth] = useState();
  const [entryPosition, setEntryPosition] = useState();
  const hasImage = showImage && width >= minWidthForImage && !program.imageIsFallbackUrl;
  return /*#__PURE__*/_jsxs(Layout, {
    ref: itemRef,
    isNow: isNow,
    isActive: isActive,
    style: {
      left: `${left}px`,
      width: !minWidth || width > minWidth ? `${width}px` : 'auto',
      minWidth: minWidth ? `${minWidth}px` : 'inherit'
    },
    role: "button",
    onClick: () => onSelectProgram({
      program,
      channel
    }),
    tabIndex: program.tabIndex ?? -1,
    "aria-label": `
        ${channel.title} - 
        ${program.title} - 
        ${program.subtitle ? `${program.subtitle} - ` : ''}
        ${formatTime(program.startTime, t)} 
        ${t('date.range.to')} 
        ${formatTime(program.endTime, t)} - 
        ${t('global.moreInformation')}`,
    onKeyDown: event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelectProgram({
          program,
          channel
        });
      }
    },
    onMouseEnter: () => {
      if (itemRef?.current && scrollRef?.current) {
        const {
          left: scrollLeft,
          right: scrollRight
        } = scrollRef.current.getBoundingClientRect();
        const {
          left: itemLeft,
          right: itemRight
        } = itemRef.current.getBoundingClientRect();
        let offset = 0;
        if (itemLeft - gridChannelWidth < scrollLeft) {
          // The entry is not fully visible on the left side
          // -> move it to the right!
          offset = itemLeft - gridChannelWidth - scrollLeft;
        } else if (scrollRight - itemLeft < hoverItemWidth) {
          // The entry is not fully visible on the right side
          // -> move it to the left!
          offset = itemLeft - scrollRight + hoverItemWidth;
        }
        setEntryPosition({
          left: itemLeft,
          right: itemRight
        });
        setLeft(actualLeft - offset);
        setMinWidth(hoverItemWidth);
      }
    },
    onMouseMove: event => {
      // Stop hovering when the pointer reaches the non-hover right position.
      // This allows hovering over the next, potentially underlying item.
      if (entryPosition && event.clientX > entryPosition.right) {
        setEntryPosition(undefined);
        setMinWidth(undefined);
      }
    },
    onMouseLeave: () => {
      setEntryPosition(undefined);
      setMinWidth(undefined);
      setLeft(actualLeft);
    },
    children: [hasImage && program.imageUrl && /*#__PURE__*/_jsx(ImageContainer, {
      children: /*#__PURE__*/_jsx(PlayImage, {
        aspectRatio: tvGuideImageRatio,
        src: program.imageUrl,
        width: 320,
        alt: program.title,
        hasBorderRadius: false
      })
    }), /*#__PURE__*/_jsxs(Infos, {
      children: [/*#__PURE__*/_jsxs(Time, {
        children: [/*#__PURE__*/_jsx("span", {
          children: program.isFollowUp ? t('tvGuide.afterwards') : localTimeSpan(program.startTime, program.endTime)
        }), program.broadcastInfo && /*#__PURE__*/_jsx(InfoIcon, {
          size: 22,
          title: program.broadcastInfo,
          icon: /*#__PURE__*/_jsx(SvgInfo, {})
        })]
      }), /*#__PURE__*/_jsx(Title, {
        children: program.title
      }), /*#__PURE__*/_jsx(SubTitle, {
        children: /*#__PURE__*/_jsx(ProgramEntryPlayLink, {
          program: program,
          channel: channel,
          analyticsPageId: analyticsPageId
        })
      }), isNow && now && /*#__PURE__*/_jsx(NowMarker, {
        style: {
          width: `${now - left}px`
        }
      })]
    })]
  });
};